import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileCode2, ShieldAlert, UserCheck } from 'lucide-react';
import { ACTION_CATALOG, type ActionRisk } from '@esa/shared';
import { api, ApiError } from '../../lib/api.js';
import { Badge, Button, Callout, Field, Input, Panel, Segmented, Spinner, useToast } from '../../ui/index.js';
import { useConfigWrite } from './useConfigWrite.js';

/**
 * Which real-world changes a group has agreed to.
 *
 * WHAT THIS SCREEN DOES NOT DO, deliberately: create an action.
 *
 * An action is a catalog entry in `@esa/shared/actions.ts` — an exact MCP
 * tool name plus a schema saying what its arguments may be. That schema is
 * code, and it is doing real work: "must be a path under /Shared/", "must be
 * an @esassoc.com address", "one of Viewer, Editor, Full". A screen that
 * could add an action would have to let somebody type a tool name, and the
 * Egnyte server that exposes `egnyte_add_permission` also exposes
 * `egnyte_delete` and `egnyte_make_confidential`. At that point the catalog
 * has stopped being a boundary and become a suggestion.
 *
 * So adding or changing an action is a code change and a deploy, reviewed
 * like one. This screen governs what a group may USE, which is the decision
 * that actually varies between groups and over time.
 */

interface Row {
  key: string;
  label: string;
  risk: ActionRisk;
  enabled: boolean;
}

const RISK_TONE: Record<ActionRisk, 'neutral' | 'warn' | 'danger'> = {
  read: 'neutral',
  low: 'warn',
  high: 'danger',
};

const RISK_WORD: Record<ActionRisk, string> = {
  read: 'reads only, changes nothing',
  low: 'changes one person’s access, reversible here',
  high: 'not reversible from this system',
};

export function ActionsTab({ tenant }: { tenant: string }) {
  const write = useConfigWrite(tenant);
  const q = useQuery({
    queryKey: ['admin-actions', tenant],
    queryFn: () => api.get<{ actions: Row[] }>(`/api/v1/tenants/${tenant}/admin/actions`),
    enabled: Boolean(tenant),
  });

  if (q.isLoading) return <Spinner />;
  if (q.error) return <p className="text-xs text-ink-tertiary">Actions could not be loaded.</p>;

  const rows = q.data?.actions ?? [];
  const on = rows.filter((r) => r.enabled).length;

  return (
    <div className="space-y-4">
    <SystemOwners />
    <Panel
      title="Actions"
      subtitle={`${on} of ${rows.length} available to this group`}
      footer={
        <p className="text-2xs leading-relaxed text-ink-tertiary">
          Switching one off stops it immediately — including proposals already on screen, which
          refuse at the moment somebody tries to authorize them. Nothing is deleted: the record of
          what was done stays either way.
        </p>
      }
    >
      <Callout tone="warn" className="mb-4" icon={<ShieldAlert />}>
        These change systems outside this one. Enabling an action here does not let anybody use
        it — it still needs the <strong>Actions</strong> feature on for this group, and the person
        acting must hold the <strong>action_operator</strong> grant. Every change is proposed
        first and authorized by a named person.
      </Callout>

      <ul className="space-y-2">
        {rows.map((row) => {
          const def = ACTION_CATALOG[row.key];
          return (
            <li key={row.key} className="rounded-md border border-line-subtle bg-surface p-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-ink">{row.label}</span>
                    <Badge tone={RISK_TONE[row.risk]}>{RISK_WORD[row.risk]}</Badge>
                  </div>

                  {/* The exact tool, named. An administrator deciding whether
                      a group may do this should be able to see precisely what
                      it will call, not a friendly paraphrase of it. */}
                  {def && (
                    <p className="nums mt-1 flex items-center gap-1.5 text-2xs text-ink-tertiary">
                      <FileCode2 aria-hidden className="h-3 w-3" />
                      {def.server} · {def.tool}
                    </p>
                  )}

                  {def && (
                    <ul className="mt-1.5 space-y-0.5">
                      {Object.entries(def.argHelp).map(([name, help]) => (
                        <li key={name} className="text-2xs leading-relaxed text-ink-secondary">
                          <strong className="text-ink">{name}</strong> — {help}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <Segmented
                  value={row.enabled ? 'on' : 'off'}
                  onChange={(v) =>
                    write.mutate({
                      method: 'put',
                      path: `/api/v1/tenants/${tenant}/admin/actions/${row.key}`,
                      body: { enabled: v === 'on' },
                      done: `${row.label} is now ${v}`,
                    })
                  }
                  options={[
                    { value: 'off', label: 'Off' },
                    { value: 'on', label: 'On' },
                  ]}
                />
              </div>
            </li>
          );
        })}
      </ul>

      {/* Said plainly rather than left for somebody to discover by looking
          for an "Add action" button that is not there. */}
      <p className="mt-4 border-t border-line-subtle pt-3 text-2xs leading-relaxed text-ink-tertiary">
        The list above is fixed in code. Each entry names one tool and the rules its arguments
        must satisfy, which is what stops a request — or a model reading one — reaching a tool
        nobody intended. Adding or changing an action is a change to that code, reviewed and
        deployed; it is not something this screen can do.
      </p>
    </Panel>
    </div>
  );
}

/** How a system is named to a person. */
const SYSTEM_NAME: Record<string, string> = { egnyte: 'Egnyte' };

interface OwnerRow {
  oid: string;
  name: string | null;
  email: string;
  added_at: string;
}

/**
 * Who approves changes to each system. Every action that changes something
 * waits for one of these people; a system with nobody here can have nothing
 * approved. Platform-wide, not per group: the same people own Egnyte
 * whichever desk the request came from.
 */
function SystemOwners() {
  const qc = useQueryClient();
  const toast = useToast();
  const [email, setEmail] = useState<Record<string, string>>({});
  const q = useQuery({
    queryKey: ['system-owners'],
    queryFn: () =>
      api.get<{ systems: { system: string; owners: OwnerRow[] }[] }>('/api/v1/platform/system-owners'),
  });
  const after = () => {
    void qc.invalidateQueries({ queryKey: ['system-owners'] });
    // Open proposals change from "nobody owns" to "waiting on X".
    void qc.invalidateQueries({ queryKey: ['ticket-actions'] });
  };

  const add = useMutation({
    mutationFn: (system: string) =>
      api.post('/api/v1/platform/system-owners', { system, email: (email[system] ?? '').trim() }),
    onSuccess: (_r, system) => {
      setEmail((e) => ({ ...e, [system]: '' }));
      after();
      toast.success(`Added as an owner of ${SYSTEM_NAME[system] ?? system}`);
    },
    onError: (e: unknown) => toast.error(e instanceof ApiError ? e.message : 'That could not be added'),
  });

  const remove = useMutation({
    mutationFn: ({ system, oid }: { system: string; oid: string }) =>
      api.del(`/api/v1/platform/system-owners/${encodeURIComponent(system)}/${encodeURIComponent(oid)}`),
    onSuccess: () => {
      after();
      toast.success('Owner removed');
    },
  });

  if (q.isLoading) return <Spinner />;
  if (q.error) return <p className="text-xs text-ink-tertiary">System owners could not be loaded.</p>;

  return (
    <Panel
      title="System owners"
      subtitle="Who approves a change before anyone can run it"
      footer={
        <p className="text-2xs leading-relaxed text-ink-tertiary">
          For every group on the platform. With one owner, they may approve their own proposals
          (recorded as such); with two or more, somebody else has to.
        </p>
      }
    >
      <ul className="space-y-4">
        {(q.data?.systems ?? []).map(({ system, owners }) => {
          const label = SYSTEM_NAME[system] ?? system;
          return (
            <li key={system}>
              <p className="mb-1.5 text-sm font-medium text-ink">{label}</p>
              {owners.length === 0 ? (
                <Callout tone="warn" className="mb-2">
                  Nobody owns {label}, so no {label} action can be approved or run.
                </Callout>
              ) : (
                <ul className="mb-2 space-y-1">
                  {owners.map((o) => (
                    <li key={o.oid} className="flex items-center gap-2 text-xs">
                      <UserCheck aria-hidden className="h-3.5 w-3.5 text-brand" />
                      <span className="text-ink">{o.name ?? o.email}</span>
                      <span className="text-2xs text-ink-tertiary">{o.email}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-auto"
                        disabled={remove.isPending}
                        onClick={() => remove.mutate({ system, oid: o.oid })}
                      >
                        Remove
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex items-end gap-2">
                <Field label={`Add an owner of ${label}`}>
                  <Input
                    size="sm"
                    value={email[system] ?? ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setEmail((cur) => ({ ...cur, [system]: e.target.value }))
                    }
                    placeholder="someone@esassoc.com"
                    aria-label={`Owner of ${label}: email`}
                  />
                </Field>
                <Button
                  size="sm"
                  disabled={(email[system] ?? '').trim() === '' || add.isPending}
                  loading={add.isPending && add.variables === system}
                  onClick={() => add.mutate(system)}
                >
                  Add
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}
